import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import type { Grow } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePreferences } from "@/lib/preferences";
import { usePlantProfiles } from "@/lib/api/plant-profiles";
import { useReservoirs } from "@/lib/api/reservoirs";
import { useWaterSources } from "@/lib/api/water-sources";
import {
  ecFromMsCm,
  ecToMsCm,
  tempFromCelsius,
  tempToCelsius,
  type ECUnit,
  type TemperatureUnit,
} from "@/lib/units";

import { EnumSelect, selectClass } from "@/components/enum-select";
import {
  FEEDER_CLASS_OPTIONS,
  GROW_STATE_OPTIONS,
  SYSTEM_TYPE_OPTIONS,
} from "@/lib/catalog-options";

// Shared schema for Create + Update. Numeric chemistry windows are kept
// as strings in form state (HTML inputs are strings) and parsed at submit
// time so we don't fight RHF + zodResolver over input vs output types.
//
// Required fields validate inline; optional numerics gate on
// !Number.isNaN(...) so a blank "" stays optional, not NaN.

// Sane storage-unit limits, expressed below in the active display unit so the
// error messages match what the user sees.
//   EC: 0 .. 20 mS/cm   (≈ 0 .. 14000 ppm under ppm700)
//   Temp: -10 .. 60 °C  (≈ 14 .. 140 °F)
const EC_MAX_MS_CM = 20;
const TEMP_MIN_C = -10;
const TEMP_MAX_C = 60;

const optionalNumString = z
  .string()
  .optional()
  .refine(
    (v) => v === undefined || v === "" || !Number.isNaN(Number(v)),
    "must be a number",
  );

const growObject = z.object({
  species: z.string().min(1, "species is required"),
  count: z
    .string()
    .min(1, "required")
    .refine(
      (v) => Number.isInteger(Number(v)) && Number(v) >= 1,
      "must be ≥ 1",
    ),
  system_type: z.string().min(1, "system_type is required"),
  reservoir_id: z.string().optional(),
  water_source_id: z.string().optional(),
  feeder_class: z.string().optional(),
  ph_min: optionalNumString,
  ph_ideal: optionalNumString,
  ph_max: optionalNumString,
  ec_min: optionalNumString,
  ec_ideal: optionalNumString,
  ec_max: optionalNumString,
  temp_min_c: optionalNumString,
  temp_ideal_c: optionalNumString,
  temp_max_c: optionalNumString,
  current_state: z.string().optional(),
  notes: z.string().optional(),
  reason: z.string().optional(),
  plant_profile_id: z.string().optional(),
});

export type GrowFormValues = z.infer<typeof growObject>;

// makeGrowSchema builds the validation schema for a given unit preference. The
// chemistry windows in form state are in DISPLAY units (ppm for EC under
// ppm700, °F for temp under F), so bounds + ordering are validated in those
// same units and the error messages read naturally. The shared rules live in
// applyChemistryRules so the plant-profile form can reuse them.
export function makeGrowSchema(tempUnit: TemperatureUnit, ecUnit: ECUnit) {
  return applyChemistryRules(growObject, tempUnit, ecUnit);
}

// growSchema kept for any importers expecting a default (SI) schema.
export const growSchema = makeGrowSchema("C", "mS/cm");

/** parseOptNum normalises a form-string field to number | undefined. */
export function parseOptNum(v: string | undefined): number | undefined {
  if (v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

// num parses a filled chemistry field to a number, or undefined when blank /
// non-numeric (those are reported by the per-field refine, so range checks skip
// them rather than double-reporting).
function num(v: string | undefined): number | undefined {
  if (v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

function ecBounds(unit: ECUnit): { min: number; max: number; label: string } {
  return {
    min: 0,
    max: ecFromMsCm(EC_MAX_MS_CM, unit),
    label: unit === "ppm700" ? "ppm" : "mS/cm",
  };
}

function tempBounds(unit: TemperatureUnit): {
  min: number;
  max: number;
  label: string;
} {
  return {
    min: tempFromCelsius(TEMP_MIN_C, unit),
    max: tempFromCelsius(TEMP_MAX_C, unit),
    label: unit === "F" ? "°F" : "°C",
  };
}

// applyChemistryRules layers range/bounds/ordering refinements onto a base
// object schema that has the ph_*/ec_*/temp_*_c string fields. Generic over the
// base shape so both the grow and plant-profile schemas can share it.
type ChemShape = {
  ph_min?: unknown;
  ph_ideal?: unknown;
  ph_max?: unknown;
  ec_min?: unknown;
  ec_ideal?: unknown;
  ec_max?: unknown;
  temp_min_c?: unknown;
  temp_ideal_c?: unknown;
  temp_max_c?: unknown;
};

export function applyChemistryRules<T extends z.ZodType>(
  base: T,
  tempUnit: TemperatureUnit,
  ecUnit: ECUnit,
) {
  const ec = ecBounds(ecUnit);
  const t = tempBounds(tempUnit);
  return base.superRefine((val, ctx) => {
    const v = val as ChemShape;
    const at = (path: string) => path;

    // Per-field bounds. Only check filled, numeric fields.
    const boundCheck = (
      key: keyof ChemShape,
      lo: number,
      hi: number,
      msg: string,
    ) => {
      const n = num(v[key] as string | undefined);
      if (n === undefined) return;
      if (n < lo || n > hi) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [at(key as string)],
          message: msg,
        });
      }
    };

    boundCheck("ph_min", 0, 14, "pH must be between 0 and 14");
    boundCheck("ph_ideal", 0, 14, "pH must be between 0 and 14");
    boundCheck("ph_max", 0, 14, "pH must be between 0 and 14");

    const ecMsg = `EC must be between ${ec.min} and ${Math.round(ec.max)} ${ec.label}`;
    boundCheck("ec_min", ec.min, ec.max, ecMsg);
    boundCheck("ec_ideal", ec.min, ec.max, ecMsg);
    boundCheck("ec_max", ec.min, ec.max, ecMsg);

    const tMsg = `Temperature must be between ${t.min} and ${t.max} ${t.label}`;
    boundCheck("temp_min_c", t.min, t.max, tMsg);
    boundCheck("temp_ideal_c", t.min, t.max, tMsg);
    boundCheck("temp_max_c", t.min, t.max, tMsg);

    // Cross-field ordering: min <= ideal <= max, comparing only filled fields.
    const order = (
      minKey: keyof ChemShape,
      idealKey: keyof ChemShape,
      maxKey: keyof ChemShape,
      label: string,
    ) => {
      const mn = num(v[minKey] as string | undefined);
      const id = num(v[idealKey] as string | undefined);
      const mx = num(v[maxKey] as string | undefined);
      if (mn !== undefined && mx !== undefined && mn > mx) {
        const m = `${label} min must be ≤ max`;
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [at(minKey as string)], message: m });
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [at(maxKey as string)], message: m });
      }
      if (mn !== undefined && id !== undefined && mn > id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [at(idealKey as string)],
          message: `${label} ideal must be ≥ min`,
        });
      }
      if (id !== undefined && mx !== undefined && id > mx) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [at(idealKey as string)],
          message: `${label} ideal must be ≤ max`,
        });
      }
    };

    order("ph_min", "ph_ideal", "ph_max", "pH");
    order("ec_min", "ec_ideal", "ec_max", "EC");
    order("temp_min_c", "temp_ideal_c", "temp_max_c", "Temp");
  });
}

// preserveOrConvert decides what storage-unit string to submit for a single
// chemistry field, fixing the quantization-drift finding. EC/temp display
// values are rounded for readability (esp. integer ppm under ppm700), so
// re-deriving storage from an UNTOUCHED display value slowly corrupts the
// stored mS/cm / °C on repeated saves. When the user did not edit the field
// (`dirty === false`) and we have its original stored value, submit that
// original verbatim — a no-edit round-trip is lossless. Otherwise convert the
// (possibly edited) display value via `convert`.
export function preserveOrConvert(
  displayValue: string | undefined,
  dirty: boolean,
  original: number | undefined,
  convert: (v: string | undefined) => string | undefined,
): string | undefined {
  if (!dirty && original !== undefined) return String(original);
  return convert(displayValue);
}

export interface GrowFormProps {
  initial?: Grow;
  submitting: boolean;
  /** Caller wires this to the appropriate mutation (create or update). */
  onSubmit: (values: GrowFormValues) => void;
  /** Top-of-form error to render (server-side validation, optimistic-lock). */
  banner?: React.ReactNode;
  submitLabel?: string;
  showReason?: boolean;
}

export function GrowForm({
  initial,
  submitting,
  onSubmit,
  banner,
  submitLabel,
  showReason = false,
}: GrowFormProps) {
  const { temperatureUnit, ecUnit } = usePreferences();
  const form = useForm<GrowFormValues>({
    resolver: zodResolver(makeGrowSchema(temperatureUnit, ecUnit)),
    defaultValues: defaultsFor(initial, temperatureUnit, ecUnit),
  });

  // Plant-profile catalog used to pre-fill chemistry targets. Selecting a
  // profile snapshots its values into the form; every field stays editable,
  // and the chosen profile id rides along as provenance.
  const profiles = usePlantProfiles();
  const profileItems = profiles.data?.items ?? [];
  const selectedProfileID = form.watch("plant_profile_id") ?? "";

  // Reservoir picker, populated from the tenant's (+ system) reservoirs.
  const reservoirs = useReservoirs();
  const reservoirItems = reservoirs.data?.items ?? [];
  const currentReservoirID = form.watch("reservoir_id") ?? "";
  const reservoirMissing =
    currentReservoirID !== "" &&
    !reservoirItems.some((r) => r.id === currentReservoirID);

  // Water-source picker — the grow's DEFAULT source for its refills (a refill
  // can still override it). Optional; "— none —" leaves it unset.
  const waterSources = useWaterSources();
  const waterSourceItems = waterSources.data?.items ?? [];
  const currentWaterSourceID = form.watch("water_source_id") ?? "";
  const waterSourceMissing =
    currentWaterSourceID !== "" &&
    !waterSourceItems.some((w) => w.id === currentWaterSourceID);

  function applyProfile(id: string) {
    form.setValue("plant_profile_id", id);
    const p = profileItems.find((x) => x.id === id);
    if (!p) return;
    const nStr = (n: number | undefined) => (n === undefined ? "" : String(n));
    const tStr = (c: number | undefined) =>
      c === undefined ? "" : tempFromCelsius(c, temperatureUnit).toFixed(1);
    const eStr = (ms: number | undefined) =>
      ms === undefined
        ? ""
        : ecUnit === "ppm700"
          ? String(Math.round(ecFromMsCm(ms, ecUnit)))
          : String(ms);
    // Applying a profile is an explicit edit of these fields, so mark them
    // dirty: submit must convert the new display values (not preserve the
    // grow's original stored value via the no-edit round-trip below).
    const dirty = { shouldDirty: true } as const;
    form.setValue("species", p.species, dirty);
    form.setValue("feeder_class", p.feeder_class ?? "", dirty);
    form.setValue("ph_min", nStr(p.ph_min), dirty);
    form.setValue("ph_ideal", nStr(p.ph_ideal), dirty);
    form.setValue("ph_max", nStr(p.ph_max), dirty);
    form.setValue("ec_min", eStr(p.ec_min), dirty);
    form.setValue("ec_ideal", eStr(p.ec_ideal), dirty);
    form.setValue("ec_max", eStr(p.ec_max), dirty);
    form.setValue("temp_min_c", tStr(p.temp_min_c), dirty);
    form.setValue("temp_ideal_c", tStr(p.temp_ideal_c), dirty);
    form.setValue("temp_max_c", tStr(p.temp_max_c), dirty);
  }

  // Temp/EC inputs hold display-unit strings (e.g. °F, ppm). Submit normalizes
  // back to storage units (Celsius, mS/cm) so storage stays SI. To avoid
  // quantization drift, an EC/temp field the user did NOT edit is submitted as
  // its original stored value rather than re-derived from the rounded display
  // (see preserveOrConvert). pH isn't unit-converted, so it's left as-is.
  const dirtyFields = form.formState.dirtyFields;
  const handleSubmit = (values: GrowFormValues) => {
    const toC = (v: string | undefined) => displayToC(v, temperatureUnit);
    const toMs = (v: string | undefined) => displayToMsCm(v, ecUnit);
    onSubmit({
      ...values,
      temp_min_c: preserveOrConvert(values.temp_min_c, !!dirtyFields.temp_min_c, initial?.temp_min_c, toC),
      temp_ideal_c: preserveOrConvert(values.temp_ideal_c, !!dirtyFields.temp_ideal_c, initial?.temp_ideal_c, toC),
      temp_max_c: preserveOrConvert(values.temp_max_c, !!dirtyFields.temp_max_c, initial?.temp_max_c, toC),
      ec_min: preserveOrConvert(values.ec_min, !!dirtyFields.ec_min, initial?.ec_min, toMs),
      ec_ideal: preserveOrConvert(values.ec_ideal, !!dirtyFields.ec_ideal, initial?.ec_ideal, toMs),
      ec_max: preserveOrConvert(values.ec_max, !!dirtyFields.ec_max, initial?.ec_max, toMs),
    });
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="space-y-6 max-w-2xl"
      noValidate
    >
      {banner}

      <input type="hidden" {...form.register("plant_profile_id")} />
      <Field
        label="Start from plant profile"
        help="Pre-fills species and chemistry targets from a system or saved profile. You can still edit every field below."
      >
        <select
          className={selectClass}
          value={selectedProfileID}
          onChange={(e) => applyProfile(e.target.value)}
        >
          <option value="">— none —</option>
          {profileItems.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.system_provided ? " (system)" : ""}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Species" error={form.formState.errors.species?.message}>
          <Input {...form.register("species")} autoFocus />
        </Field>
        <Field label="Count" error={form.formState.errors.count?.message}>
          <Input type="number" min={1} {...form.register("count")} />
        </Field>
        <EnumSelect
          label="System type"
          name="system_type"
          form={form}
          options={SYSTEM_TYPE_OPTIONS}
          required
          placeholder="— select —"
          error={form.formState.errors.system_type?.message}
        />
        <EnumSelect
          label="Feeder class"
          name="feeder_class"
          form={form}
          options={FEEDER_CLASS_OPTIONS}
          placeholder="— unset —"
        />
        <Field label="Reservoir">
          <select className={selectClass} {...form.register("reservoir_id")}>
            <option value="">— none —</option>
            {reservoirItems.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.container_type ? ` (${r.container_type})` : ""}
              </option>
            ))}
            {reservoirMissing && (
              <option value={currentReservoirID}>
                {currentReservoirID} (current)
              </option>
            )}
          </select>
        </Field>
        <Field
          label="Water source"
          help="Optional default for this grow's refills (overridable per refill)."
        >
          <select className={selectClass} {...form.register("water_source_id")}>
            <option value="">— none —</option>
            {waterSourceItems.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
            {waterSourceMissing && (
              <option value={currentWaterSourceID}>
                {currentWaterSourceID} (current)
              </option>
            )}
          </select>
        </Field>
        <EnumSelect
          label="Current state"
          name="current_state"
          form={form}
          options={GROW_STATE_OPTIONS}
          placeholder="— select —"
        />
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Chemistry targets</legend>
        <RangeRow label="pH" name="ph" form={form} />
        <RangeRow
          label={`EC (${ecUnit === "ppm700" ? "ppm" : "mS/cm"})`}
          name="ec"
          form={form}
        />
        <RangeRow
          label={`Temp (${temperatureUnit})`}
          name="temp"
          form={form}
          suffix="_c"
        />
      </fieldset>

      <Field label="Notes">
        <Textarea rows={3} {...form.register("notes")} />
      </Field>

      {showReason && (
        <Field
          label="Reason for change"
          help="Captured in the audit log."
          error={form.formState.errors.reason?.message}
        >
          <Input {...form.register("reason")} />
        </Field>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : (submitLabel ?? "Save")}
        </Button>
      </div>
    </form>
  );
}

function defaultsFor(
  g: Grow | undefined,
  tempUnit: TemperatureUnit,
  ecUnit: ECUnit,
): GrowFormValues {
  if (!g) {
    return {
      species: "",
      count: "1",
      system_type: "",
      current_state: "active",
      notes: "",
    };
  }
  const nStr = (n: number | undefined) => (n === undefined ? "" : String(n));
  // Temp/EC values come back from the API in storage units (Celsius, mS/cm).
  // Render them in the preferred display unit so the user reads + edits in it.
  const tStr = (c: number | undefined) =>
    c === undefined ? "" : tempFromCelsius(c, tempUnit).toFixed(1);
  const eStr = (ms: number | undefined) =>
    ms === undefined
      ? ""
      : ecUnit === "ppm700"
        ? String(Math.round(ecFromMsCm(ms, ecUnit)))
        : String(ms);
  return {
    species: g.species,
    count: String(g.count),
    system_type: g.system_type,
    reservoir_id: g.reservoir_id ?? "",
    water_source_id: g.water_source_id ?? "",
    feeder_class: g.feeder_class ?? "",
    ph_min: nStr(g.ph_min),
    ph_ideal: nStr(g.ph_ideal),
    ph_max: nStr(g.ph_max),
    ec_min: eStr(g.ec_min),
    ec_ideal: eStr(g.ec_ideal),
    ec_max: eStr(g.ec_max),
    temp_min_c: tStr(g.temp_min_c),
    temp_ideal_c: tStr(g.temp_ideal_c),
    temp_max_c: tStr(g.temp_max_c),
    current_state: g.current_state ?? "",
    notes: g.notes ?? "",
    plant_profile_id: g.plant_profile_id ?? "",
  };
}

// displayToC converts a display-unit form string back to the Celsius string
// the API expects. Empty stays empty (treated as "unset"). NaN stays as the
// original so zod's refine still catches it.
function displayToC(
  v: string | undefined,
  unit: TemperatureUnit,
): string | undefined {
  if (v === undefined || v === "") return v;
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return tempToCelsius(n, unit).toFixed(2);
}

// displayToMsCm converts a display-unit EC string back to the mS/cm string the
// API expects. Empty/NaN pass through so zod's refine still applies.
function displayToMsCm(
  v: string | undefined,
  unit: ECUnit,
): string | undefined {
  if (v === undefined || v === "") return v;
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return ecToMsCm(n, unit).toString();
}

function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
      {help && !error && (
        <p className="text-xs text-muted-foreground">{help}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

import type { UseFormReturn } from "react-hook-form";

function RangeRow({
  label,
  name,
  form,
  suffix = "",
}: {
  label: string;
  name: "ph" | "ec" | "temp";
  form: UseFormReturn<GrowFormValues>;
  suffix?: string;
}) {
  type Key = keyof GrowFormValues;
  const minKey = (name + "_min" + suffix) as Key;
  const idealKey = (name + "_ideal" + suffix) as Key;
  const maxKey = (name + "_max" + suffix) as Key;
  const errors = form.formState.errors;
  // De-dup messages (an inverted range reports the same text on min + max).
  const messages = Array.from(
    new Set(
      [errors[minKey]?.message, errors[idealKey]?.message, errors[maxKey]?.message].filter(
        (m): m is string => typeof m === "string",
      ),
    ),
  );
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-3 items-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Input
          type="number"
          step="any"
          placeholder="min"
          {...form.register(minKey)}
        />
        <Input
          type="number"
          step="any"
          placeholder="ideal"
          {...form.register(idealKey)}
        />
        <Input
          type="number"
          step="any"
          placeholder="max"
          {...form.register(maxKey)}
        />
      </div>
      {messages.map((m) => (
        <p key={m} className="text-xs text-destructive">
          {m}
        </p>
      ))}
    </div>
  );
}
